import React, {Dispatch, useContext} from 'react'
import {
    columnsContainsWater,
    getExtraction,
    playFieldReducer,
    PlayFieldState,
    PlayFieldStateAction,
    toConvertSize
} from './playFieldState'
import ViewBlock from './ViewBlock'
import ProductLookUpResponse from '../../controllers/ProductLookUpResponse'
import ElementValues from './Helpers'
import LeafSummary from './LeafSummary'
import SoilSummary from './SoilSummary'
import Withdrawal from './Withdrawal'
import AppContext from '../../appContext'
import PlayfieldTemplate from './PlayfieldTemplate'
import {PlayFieldExtra} from './Program'
import {customUnits, unitCustom} from './CustomUnits'
import PhenologyBlock from './PhenologyBlock'
import PlayFieldProgram from '../../controllers/PlayFieldProgram'
import GenerateBy from '../../controllers/GenerateBy'
import Notes from './Notes'
import {TotalsRow} from "./TotalsRow";
import PricingColumn from "../../controllers/PricingColumn";

// ---------------------------------------------
// The main PlayField component
type PlayFieldProps = {
    extra: PlayFieldExtra
    setExtra: (extra: Partial<PlayFieldExtra>, state?: PlayFieldState) => void

    state: PlayFieldState
    stateDispatch: Dispatch<PlayFieldStateAction>

    products: ProductLookUpResponse[]

    program: PlayFieldProgram
    addProduct: (product: ProductLookUpResponse, applyToAll: boolean) => void
    editProduct: (
        columnIndex: number,
        product: ProductLookUpResponse,
        applyToAll: boolean
    ) => void
}

const PlayField: React.FC<PlayFieldProps> = (props) => {
    const {
        initial: { system },
        word,
    } = useContext(AppContext)

    function saveNotes(note: string): void {
        props.setExtra({ notes: note })
    }

    function updateGenerateBy(generateBy: GenerateBy): void {
        props.setExtra({
            viewBlock: { ...props.extra.viewBlock, generateBy },
        })
    }

    // Pricing columns array
    const columns = props.state.calculations.pricingColumns;

    const { calculations } = props.state

    const show = (index: number, check: (product: ProductLookUpResponse) => boolean) => {
        const col = props.state.calculations.pricingColumns[index];
        return col?.type == 'column' && check(col.data.product);
    }
    
    const showSolid = (index: number) => show(index, p => p.solid || !props.program.splitUnits);
    const showLiquid = (index: number) => show(index, p => p.liquid);

    return (
        <div className="mx-3 mb-1 border-2 border-gray-100">
            <LeafSummary
                data={props.extra.leaf}
                setData={(leaf) => props.setExtra({ leaf })}
            />

            <SoilSummary
                data={props.extra.ground}
                setData={(ground) => props.setExtra({ ground })}
            />

            <PlayfieldTemplate
                state={[props.state, props.stateDispatch]}
                products={props.products}
                program={props.program}
                addProduct={props.addProduct}
                editProduct={props.editProduct}
                currentGenerateBy={props.extra.viewBlock.generateBy}
                updateGenerateBy={updateGenerateBy}
                render={{
                    viewBlock: () => (
                        <ViewBlock
                            farmName={props.program.farmName}
                            data={props.extra.viewBlock}
                            waterUsed={columnsContainsWater(props.state.data.columns)}
                            update={(viewBlock) =>
                                props.setExtra(
                                    { viewBlock },
                                    playFieldReducer(props.state, {
                                        type: 'recalculateCells',
                                        convertSize: toConvertSize(viewBlock),
                                    })
                                )
                            }
                        />
                    ),
                    extractions: (rowIndex: number) => (
                        <ElementValues
                            system={system}
                            values={getExtraction(props.state, rowIndex)}
                            emptyColor="text-white"
                        />
                    ),
                    totals: () => (
                        <>
                            {/* 1) Standard totals (kg/ha or lb/ac) */}
                            <TotalsRow
                                label={`${word('total')} ${unitCustom(system, customUnits.kgha_lbAc)}`}
                                totals={calculations.standardTotals}
                                elementTotals={calculations.elementStdTotals}
                                columns={columns}
                                system={system}
                                show={showSolid}
                                pricing={props.program.pricingModule}
                                emptyColor="text-gray-300"
                            />

                            {/* 2) Land totals (kg/land or lb/land) */}
                            <TotalsRow
                                label={`${word('total')} ${unitCustom(system, customUnits.kgLand_lbLand)}`}
                                totals={calculations.landTotals}
                                elementTotals={calculations.elementLandTotals}
                                columns={columns}
                                system={system}
                                show={showSolid}
                                pricing={props.program.pricingModule}
                                emptyColor="text-gray-300"
                                
                            />

                            {/* 3) Liquid totals (L/ha or gallons/ac) if splitUnits is true */}
                            {props.program.splitUnits && (
                                <>
                                    <TotalsRow
                                        label={`${word('total')} ${unitCustom(system, customUnits.lHa_gallonAc)}`}
                                        totals={calculations.standardLiquidTotals}
                                        columns={columns}
                                        system={system}
                                        show={showLiquid}
                                        pricing={props.program.pricingModule}
                                        emptyColor="text-gray-300"
                                    />

                                    <TotalsRow
                                        label={`${word('total')} ${unitCustom(system, customUnits.lLand_gallonLand)}`}
                                        totals={calculations.landLiquidTotals}
                                        columns={columns}
                                        system={system}
                                        show={showLiquid}
                                        pricing={props.program.pricingModule}
                                        emptyColor="text-gray-300"
                                    />
                                </>
                            )}
                        </>
                    ),
                }}
            />

            <div className="flex mt-2">
                <Withdrawal
                    system={system}
                    viewBlock={props.extra.viewBlock}
                    elementTotals={props.state.calculations.elementLandTotals}
                />
                {props.program.tableGrapes && (
                    <PhenologyBlock extractions={props.state.calculations.extractions} />
                )}
            </div>

            <Notes notes={props.extra.notes} setNotes={saveNotes} />
        </div>
    )
}

export default PlayField